import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ContentShell from "components/ContentShell";
import CustomButton from "components/CustomButton";
import DeleteConsentDialog from "components/DeleteConsentDialog";
import SearchInput from "components/SearchInput";
import SimpleSwitch from "components/Switch";
import Table from "components/Table";
import { SimpleTextInput } from "components/FormTextInput";
import configData from "config/config.json";
import usePaginationHook from "hooks/usePagination";
import useSearchHook from "hooks/useSearch";
import { useSnackbar } from "hooks/useSnackbar";
import { defaultPageNo } from "utils/constants";
import { deleteQueryParam } from "utils/queryParamsHelper";
import {
  createChallengePrizeWheel,
  deleteChallengePrizeWheel,
  getChallengePrizeWheels,
  updateChallengePrizeWheel,
} from "services/challengeService";

const SEGMENT_COUNT = 12;

interface Segment {
  coins: number | null;
  probability: number | null;
}

interface PrizeWheel {
  _id?: string;
  name: string;
  isActive: boolean;
  segments: Segment[];
}

const useStyles = makeStyles(() => ({
  container: {
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
  },
}));

/** A fresh draft always starts with exactly SEGMENT_COUNT empty slices. */
const emptyWheel = (): PrizeWheel => ({
  name: "",
  isActive: true,
  segments: Array.from({ length: SEGMENT_COUNT }, () => ({
    coins: null,
    probability: null,
  })),
});

/** Force any incoming segment list to exactly SEGMENT_COUNT rows (pad short, trim long). */
const normalizeToTwelve = (segments: Segment[] = []): Segment[] => {
  const rows = segments
    .slice(0, SEGMENT_COUNT)
    .map((s) => ({ coins: s?.coins ?? null, probability: s?.probability ?? null }));
  while (rows.length < SEGMENT_COUNT) {
    rows.push({ coins: null, probability: null });
  }
  return rows;
};

const sumProbabilities = (segments: Segment[]) =>
  segments.reduce((total, segment) => total + (Number(segment.probability) || 0), 0);

interface RowActionsProps {
  data: PrizeWheel;
  actionHandlers: {
    handleEdit?: (wheel: PrizeWheel) => void;
    handleDelete?: (wheel: PrizeWheel) => void;
    handleCopyId?: (id?: string) => void;
  };
}

const RowActions = ({ data, actionHandlers }: RowActionsProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { handleEdit = () => {}, handleDelete = () => {}, handleCopyId = () => {} } =
    actionHandlers;

  const close = (event?: any) => {
    event?.stopPropagation?.();
    setAnchorEl(null);
  };

  const handleClick = (event: any) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  return (
    <>
      <IconButton onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={close}>
        <MenuItem
          onClick={(e) => {
            close(e);
            handleEdit(data);
          }}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            close(e);
            handleDelete(data);
          }}
        >
          Delete
        </MenuItem>
      </Menu>
    </>
  );
};

/**
 * PBF-coin prize wheels behind the COIN_PRIZE_WHEEL challenge reward. Each wheel has exactly 12
 * slices — a coin value and a drop rate each; the backend draws the winning slice server-side when
 * a player claims, so the drop rates configured here are never sent to the game client.
 */
const ChallengePrizeWheels = () => {
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();
  const { searchValue, onSearch: _onSearch } = useSearchHook();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [wheels, setWheels] = useState<PrizeWheel[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [editorOpen, setEditorOpen] = useState(false);
  const [draft, setDraft] = useState<PrizeWheel>(emptyWheel());
  const [deleteTarget, setDeleteTarget] = useState<PrizeWheel | null>(null);

  const { onChangePage, onPageSizeChange, pageNo, pageSize } = usePaginationHook({
    totalItems,
  });

  const probabilityTotal = sumProbabilities(draft.segments);
  const probabilityIsValid = Math.abs(probabilityTotal - 100) < 0.01;

  const onSearch = (value: any) => {
    onChangePage(defaultPageNo);
    _onSearch(value);
  };

  const fetchWheels = async () => {
    try {
      setLoading(true);
      const payload: any = { pageNo, pageSize };
      if (searchValue) payload.search = searchValue;
      const resp = await getChallengePrizeWheels(payload);
      setWheels(resp?.data?.items || []);
      setTotalItems(resp?.data?.totalItems ?? 0);
    } catch (error: any) {
      showSnackbar(error.message || "Failed to load prize wheels", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWheels();
  }, [pageNo, pageSize, searchValue]);

  const copyId = async (id?: string) => {
    if (!id) return;
    try {
      await navigator.clipboard.writeText(id);
      showSnackbar("Prize wheel ID copied", "success");
    } catch {
      showSnackbar("Could not copy ID", "error");
    }
  };

  const openCreate = () => {
    setDraft(emptyWheel());
    setEditorOpen(true);
  };

  const openEdit = (wheel: PrizeWheel) => {
    setDraft({
      _id: wheel._id,
      name: wheel.name,
      isActive: wheel.isActive ?? true,
      segments: normalizeToTwelve(wheel.segments),
    });
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    deleteQueryParam("edit", setSearchParams);
  };

  // The editor is a dialog rather than its own route, so the wheel being edited is carried in the
  // URL. That is what lets a row be opened in a second tab and still land on that wheel's editor.
  const editId = searchParams.get("edit");
  const openedEditId = useRef<string | null>(null);

  useEffect(() => {
    if (!editId) {
      openedEditId.current = null;
      return;
    }
    if (openedEditId.current === editId) return;
    const wheel = wheels.find((item) => item._id === editId);
    if (!wheel) return;
    openedEditId.current = editId;
    openEdit(wheel);
  }, [editId, wheels]);

  const rowHref = (data: any) => {
    const params = new URLSearchParams(searchParams);
    params.set("edit", data?._id);
    return `${configData.routes.challenges.prizeWheels}?${params.toString()}`;
  };

  const updateSegment = (index: number, field: keyof Segment, value: string) => {
    setDraft((prev) => {
      const segments = [...prev.segments];
      segments[index] = {
        ...segments[index],
        [field]: value === "" ? null : Number(value),
      };
      return { ...prev, segments };
    });
  };

  const save = async () => {
    try {
      if (!draft.name.trim()) {
        throw new Error("Name is required");
      }
      if (draft.segments.length !== SEGMENT_COUNT) {
        throw new Error(`A wheel must have exactly ${SEGMENT_COUNT} segments`);
      }
      draft.segments.forEach((segment, index) => {
        if (segment.coins === null || Number(segment.coins) < 0) {
          throw new Error(`Segment #${index + 1}: coin value is required and cannot be negative`);
        }
        if (segment.probability === null || Number(segment.probability) < 0) {
          throw new Error(`Segment #${index + 1}: drop rate is required and cannot be negative`);
        }
      });
      if (!probabilityIsValid) {
        throw new Error(
          `Drop rates must add up to 100 — they currently add up to ${probabilityTotal}`,
        );
      }

      setLoading(true);

      const payload = {
        ...(draft._id ? { _id: draft._id } : {}),
        name: draft.name.trim(),
        isActive: draft.isActive,
        segments: draft.segments.map((segment) => ({
          coins: Number(segment.coins),
          probability: Number(segment.probability),
        })),
      };

      if (draft._id) {
        await updateChallengePrizeWheel(payload);
        showSnackbar("Prize wheel updated", "success");
      } else {
        await createChallengePrizeWheel(payload);
        showSnackbar("Prize wheel created", "success");
      }

      closeEditor();
      await fetchWheels();
    } catch (error: any) {
      showSnackbar(error.message || "Failed to save prize wheel", "error");
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget?._id) return;
    try {
      setLoading(true);
      await deleteChallengePrizeWheel(deleteTarget._id);
      showSnackbar("Prize wheel deleted", "success");
      setDeleteTarget(null);
      await fetchWheels();
    } catch (error: any) {
      showSnackbar(error.message || "Failed to delete prize wheel", "error");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      id: "_id",
      label: "ID (use in challenge)",
      alignHead: "center",
      alignBody: "center",
      renderCell: (row: PrizeWheel) => (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
          <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
            {row._id}
          </Typography>
          <Tooltip title="Copy ID">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                copyId(row._id);
              }}
            >
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    {
      id: "name",
      label: "Name",
      isSortable: true,
      alignHead: "center",
      alignBody: "center",
    },
    {
      id: "segmentCount",
      label: "Segments",
      alignHead: "center",
      alignBody: "center",
      renderCell: (row: PrizeWheel) => row?.segments?.length ?? 0,
    },
    {
      id: "coinValues",
      label: "Segments — coins · drop rate",
      alignHead: "center",
      alignBody: "center",
      renderCell: (row: PrizeWheel) => {
        const segments = row?.segments || [];
        if (!segments.length) return "—";
        return (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.75,
              justifyContent: "center",
              alignItems: "center",
              py: 0.5,
            }}
          >
            {segments.map((s, i) => (
              <Box
                key={i}
                sx={{
                  display: "inline-flex",
                  alignItems: "baseline",
                  gap: 0.5,
                  px: 1,
                  py: 0.4,
                  borderRadius: 1.5,
                  bgcolor: "#f8fafc",
                  border: "1px solid #e5e7eb",
                  boxShadow: "0 1px 1.5px rgba(15,23,42,0.04)",
                  fontSize: 11.5,
                  lineHeight: 1.5,
                  whiteSpace: "nowrap",
                }}
              >
                <Box component="span" sx={{ fontWeight: 700, color: "#1e293b" }}>
                  {s.coins}
                </Box>
                <Box component="span" sx={{ color: "#cbd5e1", fontSize: 10 }}>
                  |
                </Box>
                <Box component="span" sx={{ fontWeight: 600, color: "#6366f1" }}>
                  {s.probability}%
                </Box>
              </Box>
            ))}
          </Box>
        );
      },
    },
    {
      id: "status",
      label: "Status",
      alignHead: "center",
      alignBody: "center",
      renderCell: (row: PrizeWheel) => (
        <Chip
          size="small"
          label={row.isActive ? "Active" : "Disabled"}
          color={row.isActive ? "success" : "default"}
        />
      ),
    },
    { id: "actions", label: "Actions" },
  ];

  const renderSegment = (index: number) => {
    const segment = draft.segments[index];
    return (
      <Box key={index} sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1.25 }}>
        <Typography sx={{ width: 26, fontWeight: 700, color: "text.secondary" }}>
          {index + 1}
        </Typography>
        <Box sx={{ flex: 1 }}>
          <SimpleTextInput
            label="Coins"
            name={`coins_${index}`}
            type="number"
            value={segment.coins ?? ""}
            onChange={(e: any) => updateSegment(index, "coins", e.target.value)}
            placeholder="e.g. 250"
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <SimpleTextInput
            label="Drop rate (%)"
            name={`probability_${index}`}
            type="number"
            value={segment.probability ?? ""}
            onChange={(e: any) => updateSegment(index, "probability", e.target.value)}
            placeholder="e.g. 8.33"
          />
        </Box>
      </Box>
    );
  };

  const firstHalf = Array.from({ length: SEGMENT_COUNT / 2 }, (_, i) => i);
  const secondHalf = Array.from({ length: SEGMENT_COUNT / 2 }, (_, i) => i + SEGMENT_COUNT / 2);

  return (
    <ContentShell
      title="Challenge Prize Wheels"
      loading={loading}
      actions={
        <Box style={{ display: "flex", flexFlow: "row nowrap", gap: 4 }}>
          <SearchInput
            value={searchValue}
            placeholder="Search prize wheels"
            onSearch={onSearch}
          />
          <CustomButton onClick={openCreate}>New Prize Wheel</CustomButton>
        </Box>
      }
    >
      <Box className={styles.container}>
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          A prize wheel is reusable — copy its <b>ID</b> and paste it on any challenge whose reward
          type is <b>Coin Prize Wheel</b>. Every wheel has exactly <b>{SEGMENT_COUNT} segments</b> and
          the drop rates must add up to 100. The winning slice is drawn on the server when the player
          claims, so the drop rates never reach the game client.
        </Alert>

        <Table
          columns={columns}
          data={wheels}
          currentPage={pageNo}
          pageSize={pageSize}
          totalEntries={totalItems}
          highlightAltRow={true}
          actions={RowActions}
          rowHref={rowHref}
          onChangePage={onChangePage}
          onRowsPerPageChange={onPageSizeChange}
          actionHandlers={{
            handleEdit: openEdit,
            handleDelete: setDeleteTarget,
            handleCopyId: copyId,
          }}
        />
      </Box>

      <Dialog open={editorOpen} onClose={closeEditor} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {draft._id ? "Edit Prize Wheel" : "New Prize Wheel"}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
              <Box sx={{ flex: 1, minWidth: 260 }}>
                <SimpleTextInput
                  label="Name"
                  name="name"
                  value={draft.name}
                  onChange={(e: any) => setDraft((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g. Weekend Coin Wheel"
                />
              </Box>
              <SimpleSwitch
                name="isActive"
                label="Active"
                value={draft.isActive}
                helperText="Only active wheels can be selected on a challenge."
                onChange={() => setDraft((prev) => ({ ...prev, isActive: !prev.isActive }))}
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                Segments ({SEGMENT_COUNT})
              </Typography>

              {/* Two fixed columns of 6 so all 12 slices are visible in one view without scrolling. */}
              <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                <Box sx={{ flex: 1, minWidth: 320 }}>{firstHalf.map(renderSegment)}</Box>
                <Box sx={{ flex: 1, minWidth: 320 }}>{secondHalf.map(renderSegment)}</Box>
              </Box>

              <Alert severity={probabilityIsValid ? "success" : "warning"} sx={{ mt: 1 }}>
                Drop rates add up to {probabilityTotal}
                {probabilityIsValid ? " — ready to save." : " — they must add up to exactly 100."}
              </Alert>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={closeEditor}>Cancel</Button>
          <CustomButton onClick={save} disabled={loading}>
            {draft._id ? "Save Changes" : "Create Wheel"}
          </CustomButton>
        </DialogActions>
      </Dialog>

      <DeleteConsentDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        itemName={deleteTarget?.name}
        loading={loading}
      />
    </ContentShell>
  );
};

export default ChallengePrizeWheels;
